import { get } from 'svelte/store';
import { html } from 'common-tags';
import { Liquid } from 'liquidjs';

import { getTemplateDefaults } from '@client/stores/bizcard';
import { activeDomain, isBambooHR, isGoogle, isMicrosoft } from '@client/stores/domain-settings';
import { userStore } from '@client/stores/user';

import basic from './basic';
import classic from './classic';
import innovate from './innovate';
import minimal from './minimal';
import modern from './modern';

/**
 * @typedef {import('../types').BizcardPreview} BizcardPreview
 * @typedef {import('../types').Brand} Brand
 */

const engine = new Liquid();

const templates = {
  basic,
  classic,
  innovate,
  minimal,
  modern
};

/**
 * Generates a JSON string for a web manifest.
 * @param {string} imageUrl - The URL for the user's profile image (used for icons).
 * @param {Brand} brand - The brand object containing theme colors.
 * @returns {string} A JSON string representing the manifest.json file.
 */
export const loadBizcardManifest = (imageUrl, brand) => {
  return JSON.stringify({
    id: '/bizcard/{{ sgsusername }}',
    name: '{{ name }}',
    short_name: '{{ name }}',
    description: 'My Business Card',
    start_url: '/bizcard/{{ sgsusername }}',
    scope: '/bizcard/',
    display: 'standalone',
    prefer_related_applications: false,
    background_color: brand.colorPrimary,
    theme_color: brand.colorPrimary,
    icons: [
      {
        src: imageUrl,
        sizes: '192x192',
        type: 'image/png',
        purpose: 'any'
      },
      {
        src: imageUrl,
        sizes: '512x512',
        type: 'image/png',
        purpose: 'any'
      }
    ]
  });
};

/**
 * Loads and constructs the full HTML for a business card template.
 * @param {string} name - The name of the template to use (e.g., 'classic', 'modern').
 * @param {string} smartUrl - The base URL for the business card.
 * @param {string} imageUrl - The URL for the user's profile image.
 * @param {Brand} brand - The brand object with theme colors, fonts, and assets.
 * @returns {string} A complete HTML document string.
 */
export const loadBizcardTemplate = (name, smartUrl, imageUrl, brand) => {
  const { body, style } = templates[name](brand);
  // prettier-ignore
  const template = html`
    {% assign website = website | default: '${brand.companyUrl}'%}
    {% capture image_url %}${imageUrl}{% endcapture%}
    <!doctype html>
    <html lang="en">
      <head>
        <base href="${smartUrl}/bizcard/{{ sgsusername }}/" />
        <meta charset="utf-8" />
        <title>{{ name }}</title>
        <link rel="shortcut icon" type="image/x-icon" href="{{ image_url }}" id="favicon" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="manifest" href="manifest.json" />

        <meta name="theme-color" content="${brand.colorPrimary}" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content="{{ name }}" />
        <link rel="apple-touch-icon" href="{{ image_url }}" />
        <link rel="apple-touch-startup-image" href="${brand.iconUrl}" />

        <link rel="stylesheet" href="https://assets.opensense.com/business-cards/reset.css" />
        <link rel="stylesheet" href="${brand.fontsUrl}" />
        <link rel="stylesheet" href="https://cdn.icomoon.io/64130/os-icons/style.css?xrerli" />
        ${style}
      </head>
      <body>
        ${body}
        <script type="text/javascript" src="https://assets.opensense.com/business-cards/bizcard-components.js?v=20250730"></script>
      </body>
    </html>
  `;

  return template;
};

/**
 * Asynchronously generates preview data for all available business card templates.
 * @returns {Promise<BizcardPreview[]>} A promise that resolves to an array of template preview objects.
 */
export const loadNewBizcardModalTemplates = async () => {
  const templateNames = ['classic', 'modern', 'innovate', 'minimal', 'basic'];

  const promises = templateNames.map(async (name) => {
    let user, microsoft, google, bamboo, domain;

    // This pattern gets the *current* value from Svelte stores and immediately unsubscribes.
    const unsubscribers = [
      userStore.subscribe((value) => (user = value)),
      isMicrosoft.subscribe((value) => (microsoft = value)),
      isGoogle.subscribe((value) => (google = value)),
      isBambooHR.subscribe((value) => (bamboo = value)),
      activeDomain.subscribe((value) => (domain = value))
    ];

    unsubscribers.forEach((unsub) => unsub());

    const platform = get(isMicrosoft)
      ? 'office365'
      : get(isGoogle)
        ? 'google'
        : get(isBambooHR)
          ? 'bamboohr'
          : undefined;
    const [sgcnameproto, sgcname] = user.cname;
    const smartUrl = `${sgcnameproto}://${sgcname}`;
    const brandDefaults = getTemplateDefaults();

    const previewUser = user.isManaging
      ? {
          username: 'jane.doe',
          name: 'Jane Doe',
          title: 'Director',
          email: `jane.doe@${domain}`,
          phone: '(123) 456-7890',
          profilePicture: `/photo/jane.doe/example.jpg`
        }
      : {
          username: user.currentUser.username,
          name: user.currentUser.name,
          title: user?.currentUser?.title || 'Employee',
          email: user?.currentUser?.email || `user@${domain}`,
          phone: user?.currentUser?.mobile || '(123) 456-7890',
          profilePicture: `/photo/${user.currentUser.username}/${platform}.jpg`
        };

    const previewData = {
      ...previewUser,
      sgcnameproto,
      sgcname,
      sgsusername: previewUser?.username
    };

    const rawTemplate = loadBizcardTemplate(name, smartUrl, previewUser.profilePicture, brandDefaults);

    const parsedTemplate = engine.parse(rawTemplate);
    const renderedTemplate = await engine.render(parsedTemplate, previewData);

    return {
      id: name,
      name,
      width: 380,
      height: 808,
      html: renderedTemplate
    };
  });

  const previewTemplates = await Promise.all(promises);

  return previewTemplates;
};
